import type { Editor } from '@tiptap/core';
import * as React from 'react';
import { getBlockStart, moveImageToBlock, selectImageAt } from './imageCommands';

/** Pointer travel that turns a press into a drag, so a plain click still selects the image. */
const DRAG_THRESHOLD_PX = 4;
const AUTO_SCROLL_EDGE_PX = 48;
const AUTO_SCROLL_STEP_PX = 16;

interface DropTarget {
	index: number;
	/** Indicator offset from the top of the scrolling surface's content. */
	top: number;
}

const findDropTarget = (editor: Editor, surface: HTMLElement, clientY: number): DropTarget | null => {
	const { state, view } = editor;
	const surfaceTop = surface.getBoundingClientRect().top - surface.scrollTop;
	let lastBottom = 0;
	for (let index = 0; index < state.doc.childCount; index++) {
		const dom = view.nodeDOM(getBlockStart(state, index));
		if (!(dom instanceof HTMLElement)) continue;
		const rect = dom.getBoundingClientRect();
		if (clientY < rect.top + rect.height / 2) return { index, top: rect.top - surfaceTop };
		lastBottom = rect.bottom;
	}
	return { index: state.doc.childCount, top: lastBottom - surfaceTop };
};

const autoScroll = (surface: HTMLElement, clientY: number) => {
	const rect = surface.getBoundingClientRect();
	if (clientY < rect.top + AUTO_SCROLL_EDGE_PX) surface.scrollTop -= AUTO_SCROLL_STEP_PX;
	else if (clientY > rect.bottom - AUTO_SCROLL_EDGE_PX) surface.scrollTop += AUTO_SCROLL_STEP_PX;
};

/**
 * Drags an image to a new spot with pointer events rather than native drag and drop, which cannot tell
 * where a drop lands on these block-rendered inline images. Returns the drop indicator's offset while a
 * drag is under way.
 */
export const useImageDrag = (editor: Editor | null, surface: HTMLElement | null, enabled: boolean) => {
	const [indicatorTop, setIndicatorTop] = React.useState<number | null>(null);

	React.useEffect(() => {
		if (!editor || !surface || !enabled) return undefined;
		const root = editor.view.dom;
		let cleanupDrag: (() => void) | null = null;

		const handlePointerDown = (event: PointerEvent) => {
			if (event.button !== 0 || !(event.target instanceof HTMLImageElement)) return;
			const imagePos = editor.view.posAtDOM(event.target, 0);
			if (editor.state.doc.nodeAt(imagePos)?.type.name !== 'image') return;
			event.preventDefault();
			const startX = event.clientX;
			const startY = event.clientY;
			let isDragging = false;
			let target: DropTarget | null = null;

			const handleMove = (moveEvent: PointerEvent) => {
				if (!isDragging && Math.hypot(moveEvent.clientX - startX, moveEvent.clientY - startY) < DRAG_THRESHOLD_PX)
					return;
				isDragging = true;
				autoScroll(surface, moveEvent.clientY);
				target = findDropTarget(editor, surface, moveEvent.clientY);
				setIndicatorTop(target?.top ?? null);
			};
			const handleUp = () => {
				cleanupDrag?.();
				if (isDragging && target) {
					moveImageToBlock(editor, imagePos, target.index);
				} else {
					selectImageAt(editor, imagePos);
				}
				editor.view.focus();
			};
			cleanupDrag = () => {
				window.removeEventListener('pointermove', handleMove);
				window.removeEventListener('pointerup', handleUp);
				window.removeEventListener('pointercancel', cleanupDrag as () => void);
				cleanupDrag = null;
				setIndicatorTop(null);
			};
			window.addEventListener('pointermove', handleMove);
			window.addEventListener('pointerup', handleUp);
			window.addEventListener('pointercancel', cleanupDrag);
		};

		root.addEventListener('pointerdown', handlePointerDown);
		return () => {
			root.removeEventListener('pointerdown', handlePointerDown);
			cleanupDrag?.();
		};
	}, [editor, surface, enabled]);

	return indicatorTop;
};
